import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeaderGlobalFilter from '../filters/HeaderGlobalFilter';
import TopNavigation from './TopNavigation';
import './Header.css';

const Header = ({ userRole = 'SUPER_ADMIN', onToggleSidebar, isSidebarOpen }) => {
  const [showMobileNav, setShowMobileNav] = useState(false);
  const { user, logout } = useAuth();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true'
  );
  const profileRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle dark mode toggle
  // eslint-disable-next-line no-unused-vars
  const handleThemeToggle = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
    document.documentElement.setAttribute('data-theme', newMode ? 'dark' : 'light');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    // Try to get username from storage
    const storedUser = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');
    let username = '';

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        username = parsedUser.username || parsedUser.name || parsedUser.email || '';
      } catch (e) {
        // If parsing fails, try to use it as a string
        username = storedUser;
      }
    }

    // If we have a username from storage, use its first character
    if (username) {
      return username.charAt(0).toUpperCase();
    }

    // Fall back to user context if available
    if (user) {
      const firstName = user.firstName || user.username?.charAt(0) || '';
      const lastName = user.lastName || user.username?.charAt(1) || '';
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
    }

    // Default fallback
    return 'U';
  };

  // Get user display name
  const getUserDisplayName = () => {
    // Try to get username from storage
    const storedUser = localStorage.getItem('user_data') || sessionStorage.getItem('user_data');

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Check for different possible name fields
        if (parsedUser.firstName && parsedUser.lastName) {
          return `${parsedUser.firstName} ${parsedUser.lastName}`;
        }
        return parsedUser.username || parsedUser.name || parsedUser.email || 'User';
      } catch (e) {
        // If parsing fails, try to use it as a string
        return storedUser;
      }
    }

    // Fall back to user context if available
    if (user) {
      if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`;
      }
      return user.username || user.email || 'User';
    }

    // Default fallback
    return 'User';
  };

  const handleMobileNavToggle = () => {
    setShowMobileNav(!showMobileNav);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setIsProfileOpen(false);
  };

  // Handle profile dropdown toggle
  const toggleProfile = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  // Handle keyboard navigation for profile dropdown
  const handleProfileKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleProfile();
    } else if (event.key === 'Escape') {
      setIsProfileOpen(false);
    }
  };

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile nav on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowMobileNav(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <div className="header-blur-bg"></div>
      <header className={`app-header ${scrolled ? 'scrolled' : ''}`} role="banner">
        <div className="header-container">
          {/* Left section - Logo and brand */}
          <div className="header-left">
            <button
              className="sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              aria-expanded={isSidebarOpen}
            >
              <span className="hamburger">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            <Link to="/dashboard" className="header-brand" aria-label="Venturi IMS Home">
              <div className="brand-logo">
                <img
                  src="/venturi-logo.png"
                  alt="Venturi"
                // Height/Width removed to let CSS handle it
                />
              </div>
              <div className="brand-text">
                <span className="brand-subtitle">IMS</span>
              </div>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="mobile-menu-toggle"
            onClick={handleMobileNavToggle}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger ${showMobileNav ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {/* Top Navigation */}
          <TopNavigation
            userRole={userRole || user?.role || 'SUPER_ADMIN'}
            isOpen={showMobileNav}
            onToggle={handleMobileNavToggle}
          />

          {/* Right section - Global filter, theme toggle and user profile */}
          <div className="header-right">
            {/* Global Filter */}
            <HeaderGlobalFilter />

            {/* Theme toggle button */}
            {/* <button
            className="theme-toggle"
            onClick={handleThemeToggle}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button> */}

            {/* User profile dropdown */}
            <div className="user-profile" ref={profileRef}>
              <button
                className="profile-trigger"
                onClick={toggleProfile}
                onKeyDown={handleProfileKeyDown}
                aria-label="User menu"
                aria-expanded={isProfileOpen}
                aria-haspopup="true"
              >
                <div className="user-avatar">
                  <span className="avatar-initials">{getUserInitials()}</span>
                </div>
                <div className="user-info">
                  <span className="user-name">{getUserDisplayName()}</span>
                  <span className="user-role">{user?.role || 'User'}</span>
                </div>
                <svg
                  className={`dropdown-arrow ${isProfileOpen ? 'open' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {isProfileOpen && (
                <div className="profile-dropdown" role="menu" aria-label="User menu">
                  <div className="dropdown-header">
                    <div className="dropdown-user-info">
                      <p className="dropdown-user-name">{getUserDisplayName()}</p>
                      <p className="dropdown-user-email">{user?.email}</p>
                      <span className="dropdown-user-role">Role: {user?.role || 'User'}</span>
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-content">


                    <Link
                      to="/settings"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Settings
                    </Link>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-content">
                    <button
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                      role="menuitem"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fillRule="evenodd"
                          d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;